import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Mail, Lock, User, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login gagal. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-[#0047AB] rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-900">MyTentor</span>
            </Link>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Selamat Datang</h1>
            <p className="mt-2 text-slate-500">Masuk ke akun Anda untuk melanjutkan</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="nama@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20 rounded-xl"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20 rounded-xl"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0047AB] hover:bg-[#0047AB]/90 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="login-submit-btn"
            >
              {loading ? 'Memproses...' : 'Masuk'}
              {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
            </Button>
          </form>

          <p className="text-center text-slate-500">
            Belum punya akun?{' '}
            <Link to="/register" className="text-[#0047AB] font-medium hover:underline" data-testid="register-link">
              Daftar Sekarang
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 relative bg-[#0047AB]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0047AB] to-[#002D6A]" />
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1770146605584-76f5cabaff1b?auto=format&fit=crop&q=80&w=1200" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h2 className="text-4xl font-bold mb-4">Platform Try Out Online Terpercaya</h2>
          <p className="text-xl text-white/80">
            Persiapkan diri Anda untuk UTBK-SNBT, CPNS, TNI, POLRI, dan berbagai tes lainnya dengan ribuan soal berkualitas.
          </p>
          <div className="mt-8 flex gap-8">
            <div>
              <div className="text-3xl font-bold">16,000+</div>
              <div className="text-white/70">Soal Latihan</div>
            </div>
            <div>
              <div className="text-3xl font-bold">95%</div>
              <div className="text-white/70">Tingkat Kelulusan</div>
            </div>
            <div>
              <div className="text-3xl font-bold">3,000+</div>
              <div className="text-white/70">Siswa Aktif</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
